import { useState, useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/modules/auth'
import {
  RangerApi,
  RangerBuiltTrip,
  RangerMessage,
  RangerChatRequest,
  RangerChatResponse,
  RangerFunctionCallResult,
  RangerTool,
} from '@/shared/api/ranger'
import { ApiError } from '@/shared/api/types'
import type { TripResponse } from '@/shared/api/types'
import { tripInviteFriendsQueryKey } from '@/modules/trips/hooks/useTripInviteFriends'
import { useTripStore } from '@/stores/tripStore'

const BUILD_TRIP_GREETING =
  "Let's build it. Where are you headed and when? A place or coordinates is fine. I'll name it and write the description, and you can change anything later."

/** A message as displayed in the thread — carries UI-only fields not sent over the wire. */
export interface RangerChatMessage extends RangerMessage {
  id: string
  functionCalls?: RangerFunctionCallResult[]
  /** Written by the client, not the model. Never sent as history: Bedrock requires a user message first. */
  localOnly?: boolean
}

export interface UseRangerReturn {
  messages: RangerChatMessage[]
  sendMessage: (message: string) => Promise<void>
  loading: boolean
  error: string | null
  clearError: () => void
  clearMessages: () => void
  sessionId: string
  activeTool: RangerTool | null
  startTool: (tool: RangerTool) => void
  stopTool: () => void
  /** The trip created by the Build a trip tool in this conversation, if any. */
  builtTrip: RangerBuiltTrip | null
}

function succeededCall(calls: RangerFunctionCallResult[] | undefined, name: string) {
  return calls?.find((call) => call.name === name && call.success)
}

/**
 * Hook for interacting with Ranger AI Assistant.
 * Generates a stable sessionId per conversation; starting a new chat gets a new one.
 * @param tripId Optional trip ID for context-aware responses
 */
export function useRanger(tripId?: string): UseRangerReturn {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const [messages, setMessagesState] = useState<RangerChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTool, setActiveTool] = useState<RangerTool | null>(null)
  const [builtTrip, setBuiltTrip] = useState<RangerBuiltTrip | null>(null)

  // Mirrors state so sendMessage reads the current thread synchronously. A value captured
  // inside a setState updater isn't reliable, because React may run the updater at render.
  const messagesRef = useRef<RangerChatMessage[]>([])
  const setMessages = useCallback((next: RangerChatMessage[]) => {
    messagesRef.current = next
    setMessagesState(next)
  }, [])

  const sessionIdRef = useRef<string>(crypto.randomUUID())
  // Bumped by clearMessages, so a reply to the previous chat can't land in the new one.
  const conversationRef = useRef(0)

  const api = useMemo(() => new RangerApi(getAccessToken), [getAccessToken])

  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || loading) return

      const conversation = conversationRef.current
      const previous = messagesRef.current
      const isBuildingTrip = activeTool === 'build_trip'

      setLoading(true)
      setError(null)
      setMessages([...previous, { id: crypto.randomUUID(), role: 'user', content: message }])

      try {
        // A tool's conversation starts at its greeting. Ranger counts its questions from there,
        // and earlier chat shouldn't read as answers to them.
        const toolStart = activeTool ? previous.map((m) => Boolean(m.localOnly)).lastIndexOf(true) + 1 : 0

        // Stripped to the wire shape: id/functionCalls/localOnly are display-only
        const history: RangerMessage[] = previous
          .slice(toolStart)
          .filter((m) => !m.localOnly)
          .map(({ role, content }) => ({ role, content }))

        const request: RangerChatRequest = {
          message,
          // Building a trip targets the new trip, never whichever trip is open behind the chat.
          tripId: isBuildingTrip ? builtTrip?.id : tripId,
          sessionId: sessionIdRef.current,
          conversationHistory: history.length > 0 ? history : undefined,
          mode: activeTool ?? undefined,
          localDate: new Date().toLocaleDateString('en-CA'),
        }

        const response: RangerChatResponse = await api.chat(request)
        const functionCalls = response.metadata?.functionCalls

        const created = succeededCall(functionCalls, 'create_trip')
        if (created) {
          // Ranger can create a trip from plain chat too. Either way the chat is now about this trip.
          setBuiltTrip(created.result as RangerBuiltTrip)
          setActiveTool('build_trip')
        }

        // Ranger changed a trip the rest of the page may be showing.
        const changedTripId = created ? (created.result as RangerBuiltTrip).id : request.tripId
        const updated = succeededCall(functionCalls, 'update_trip')
        if (changedTripId && updated) {
          const { name, start_date, end_date, description } = updated.result as Partial<TripResponse>
          useTripStore.getState().updateTrip(changedTripId, {
            ...(name ? { name } : {}),
            ...(start_date ? { start_date } : {}),
            ...(end_date ? { end_date } : {}),
            ...(description ? { description } : {}),
          })
        }
        if (changedTripId && functionCalls?.some((call) => call.success)) {
          queryClient.invalidateQueries({ queryKey: ['trip', changedTripId] })
        }

        const inviteTripId = changedTripId
        if (inviteTripId && succeededCall(functionCalls, 'invite_friends')) {
          queryClient.invalidateQueries({ queryKey: tripInviteFriendsQueryKey(inviteTripId) })
        }

        if (conversation !== conversationRef.current) return

        setMessages([
          ...messagesRef.current,
          { id: crypto.randomUUID(), role: 'assistant', content: response.message, functionCalls },
        ])
      } catch (err) {
        if (conversation !== conversationRef.current) return

        setMessages(previous)

        // Never show raw backend/AI provider messages (e.g. Bedrock) to users.
        const defaultFailureMessage = 'Something went wrong with Ranger. Please try again in a few moments.'
        if (err instanceof ApiError && err.status === 403) {
          setError('Ranger AI Assistant requires a Pro subscription')
        } else if (err instanceof ApiError && err.status === 429) {
          setError('Ranger is at capacity. Please wait a moment and try again.')
        } else {
          setError(defaultFailureMessage)
        }
        throw err
      } finally {
        if (conversation === conversationRef.current) setLoading(false)
      }
    },
    [api, tripId, loading, activeTool, builtTrip, queryClient, setMessages]
  )

  const startTool = useCallback(
    (tool: RangerTool) => {
      // A fresh session too: with no history sent, the backend reloads the session's messages,
      // which would count the earlier chat against this tool's questions.
      sessionIdRef.current = crypto.randomUUID()
      setActiveTool(tool)
      setBuiltTrip(null)
      setError(null)
      setMessages([
        ...messagesRef.current,
        { id: crypto.randomUUID(), role: 'assistant', content: BUILD_TRIP_GREETING, localOnly: true },
      ])
    },
    [setMessages]
  )

  const stopTool = useCallback(() => {
    setActiveTool(null)
    setBuiltTrip(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const clearMessages = useCallback(() => {
    // A new id matters: with no history sent, the backend reloads this session's messages,
    // which would bring the old conversation back into the "new" chat.
    sessionIdRef.current = crypto.randomUUID()
    conversationRef.current += 1
    setMessages([])
    setLoading(false)
    setError(null)
    setActiveTool(null)
    setBuiltTrip(null)
  }, [setMessages])

  return {
    messages,
    sendMessage,
    loading,
    error,
    clearError,
    clearMessages,
    sessionId: sessionIdRef.current,
    activeTool,
    startTool,
    stopTool,
    builtTrip,
  }
}
